import { useQueryClient } from 'react-query'

import { handleCommonErrors } from 'utils/api'
import {
  InvalidUserNoAccessPelaksanaan,
  apiPaths,
} from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { UseMutationOptions, UseQueryOptions } from 'react-query'
import type { CommonMultiPeriodMeta } from 'types/kinerja'
import type { School } from 'types/kinerja-ks'
import type {
  ParamsUpsertPreparationDocument,
  ParamsUpsertFollowUpDocument,
  ParamsUpsertFollowUpReflectionDocument,
  Pelaksanaan,
  PelaksanaanPreparationDocument,
  ResponseFollowUpDocument,
  ResponseFollowUpEvaluationDocument,
  ResponseFollowUpReflectionDocument,
  ResponseFollowUpReflectionEvaluationDocument,
  ResponseObservationDocument,
  PelaksanaanAppealableStep,
  TPelaksanaanCompetencyImprovement,
  ParamsPostPelaksanaanCompetencyImprovement,
} from 'types/kinerja-ks-pelaksanaan'
import type { CommonResponse } from 'types/api'

export const API_GET_KS_PELAKSANAAN_QUERY_KEY = 'api-get-pelaksanaan-by-npsn'

export const API_GET_KS_COMPENTENCY_IMPROVEMENT_QUERY_KEY =
  'API_GET_KS_COMPENTENCY_IMPROVEMENT_QUERY_KEY'

export const API_GET_KS_PREPARATION_DOCUMENT_QUERY_KEY =
  'API_GET_KS_PREPARATION_DOCUMENT_QUERY_KEY'

export const API_GET_KS_OBSERVATION_DOCUMENT_QUERY_KEY =
  'API_GET_KS_OBSERVATION_DOCUMENT_QUERY_KEY'

export const API_GET_KS_FOLLOW_UP_DOCUMENT_QUERY_KEY =
  'API_GET_KS_FOLLOW_UP_DOCUMENT_QUERY_KEY'

export const API_GET_KS_FOLLOW_UP_REFLECTION_QUERY_KEY =
  'API_GET_KS_FOLLOW_UP_REFLECTION_QUERY_KEY'

export type PeriodOverride = {
  ptk_id?: string
  skp_year?: number
  skp_period?: number
}

export function useApiGetPelaksanaan({
  npsn,
  opts,
  override,
}: {
  npsn?: string
  opts?: UseQueryOptions<CommonResponse<Pelaksanaan>>
  override?: PeriodOverride
}) {
  return useKinerjaApi<CommonResponse<Pelaksanaan>>(apiPaths.pelaksanaan, {
    axiosConfig: { params: { npsn, ...override } },
    queryConfig: {
      useErrorBoundary: false,
      ...opts,
    },
    queryKey: [API_GET_KS_PELAKSANAAN_QUERY_KEY, npsn, ...[override]],
    errorHandlerFn: (status: number, path: string, error: any) => {
      if (status === 403) {
        throw new InvalidUserNoAccessPelaksanaan(path, error)
      } else {
        handleCommonErrors(status, path, error)
      }
    },
  })
}

export function useApiGetPelaksanaanCompetencyImprovement({
  npsn,
  opts,
  override,
}: {
  npsn: string
  opts?: UseQueryOptions<
    CommonResponse<TPelaksanaanCompetencyImprovement, CommonMultiPeriodMeta>
  >
  override?: PeriodOverride
}) {
  return useKinerjaApi<
    CommonResponse<TPelaksanaanCompetencyImprovement, CommonMultiPeriodMeta>
  >(`${apiPaths.pelaksanaan}/competency-improvement`, {
    axiosConfig: { params: { npsn, ...override } },
    queryConfig: {
      useErrorBoundary: false,
      ...opts,
    },
    queryKey: [
      API_GET_KS_COMPENTENCY_IMPROVEMENT_QUERY_KEY,
      npsn,
      ...(override ? [override] : []),
    ],
    errorHandlerFn: (status: number, path: string, error: any) => {
      if (status === 403) {
        throw new InvalidUserNoAccessPelaksanaan(path, error)
      } else {
        handleCommonErrors(status, path, error)
      }
    },
  })
}

export function useAPIPostPelaksanaanCompetencyImprovement(
  npsn: string,
  opts?: UseMutationOptions<
    unknown,
    Error,
    ParamsPostPelaksanaanCompetencyImprovement
  >
) {
  const queryClient = useQueryClient()
  const { periodYear: skpYear, periodNo: skpPeriod } = usePeriodContext()

  return useKinerjaMutationApi(
    `${apiPaths.pelaksanaan}/competency-improvement`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        useErrorBoundary: false,
        onSettled: () => {
          queryClient.invalidateQueries([
            API_GET_KS_COMPENTENCY_IMPROVEMENT_QUERY_KEY,
            npsn,
            skpYear,
            skpPeriod,
          ])
        },
        ...opts,
      },
    }
  )
}

// Form A
export function useAPIGetPelaksanaanPreparationDocument({
  npsn,
  opts,
  override,
}: {
  npsn?: string
  opts?: UseQueryOptions<CommonResponse<PelaksanaanPreparationDocument>>
  override?: PeriodOverride
}) {
  return useKinerjaApi<CommonResponse<PelaksanaanPreparationDocument>>(
    `${apiPaths.pelaksanaan}/preparation-document`,
    {
      axiosConfig: { params: { npsn, ...override } },
      queryConfig: { ...opts },
      queryKey: [
        API_GET_KS_PREPARATION_DOCUMENT_QUERY_KEY,
        npsn,
        ...[override],
      ],
    }
  )
}

export function useAPIUpsertPelaksanaanPreparationDocument(
  opts?: UseMutationOptions<unknown, Error, ParamsUpsertPreparationDocument>
) {
  return useKinerjaMutationApi(`${apiPaths.pelaksanaan}/preparation-document`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

// Form B
export function useAPIGetObservationDocument(
  npsn: string,
  opts: UseQueryOptions<CommonResponse<ResponseObservationDocument>>
) {
  return useKinerjaApi<CommonResponse<ResponseObservationDocument>>(
    `${apiPaths.pelaksanaan}/observation-document`,
    {
      axiosConfig: {
        params: {
          npsn,
        },
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_OBSERVATION_DOCUMENT_QUERY_KEY, npsn],
    }
  )
}

export function useAPIGetFollowUpDocument({
  npsn,
  opts,
  override,
}: {
  npsn: School['npsn']
  opts: UseQueryOptions<CommonResponse<ResponseFollowUpDocument>>
  override?: PeriodOverride
}) {
  return useKinerjaApi<CommonResponse<ResponseFollowUpDocument>>(
    `${apiPaths.pelaksanaan}/follow-up-document`,
    {
      axiosConfig: { params: { npsn, ...override } },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_FOLLOW_UP_DOCUMENT_QUERY_KEY, npsn, ...[override]],
    }
  )
}

export function useAPIGetFollowUpEvaluationDocument({
  npsn,
  options,
}: {
  npsn: School['npsn']
  options?: UseQueryOptions<CommonResponse<ResponseFollowUpEvaluationDocument>>
}) {
  return useKinerjaApi(
    `${apiPaths.pelaksanaan}/follow-up-document-evaluation`,
    {
      axiosConfig: {
        params: {
          npsn,
        },
      },
      queryConfig: {
        ...options,
      },
    }
  )
}

export function useAPIUpsertFollowUpDocument(
  opts?: UseMutationOptions<unknown, Error, ParamsUpsertFollowUpDocument>
) {
  return useKinerjaMutationApi(`${apiPaths.pelaksanaan}/follow-up-document`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

// Form D
export function useAPIGetFollowUpReflectionDocument({
  npsn,
  opts,
  override,
}: {
  npsn: School['npsn']
  opts: UseQueryOptions<CommonResponse<ResponseFollowUpReflectionDocument>>
  override?: PeriodOverride
}) {
  return useKinerjaApi<CommonResponse<ResponseFollowUpReflectionDocument>>(
    `${apiPaths.pelaksanaan}/reflection-follow-up-document`,
    {
      axiosConfig: { params: { npsn, ...override } },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [
        API_GET_KS_FOLLOW_UP_REFLECTION_QUERY_KEY,
        npsn,
        ...[override],
      ],
    }
  )
}

export function useAPIGetFollowUpReflectionEvaluationDocument({
  params,
  options,
}: {
  params?: {
    userId?: string
  }
  options?: UseQueryOptions<
    CommonResponse<ResponseFollowUpReflectionEvaluationDocument>
  >
}) {
  return useKinerjaApi<
    CommonResponse<ResponseFollowUpReflectionEvaluationDocument>
  >(`${apiPaths.pelaksanaan}/document-reflection-follow-up-evaluation`, {
    queryConfig: {
      ...options,
    },
    axiosConfig: {
      params,
    },
  })
}

export function useAPIUpsertFollowUpReflectionDocument(
  opts?: UseMutationOptions<
    unknown,
    Error,
    ParamsUpsertFollowUpReflectionDocument
  >
) {
  return useKinerjaMutationApi(
    `${apiPaths.pelaksanaan}/reflection-follow-up-document`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        useErrorBoundary: false,
        ...opts,
      },
    }
  )
}

export function useAPIPostRequestReset(
  opts?: UseMutationOptions<
    unknown,
    Error,
    { npsn: string; state: PelaksanaanAppealableStep }
  >
) {
  return useKinerjaMutationApi(`${apiPaths.pelaksanaan}/request-reset`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}
